import React, { createContext, useContext, useEffect, useState } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { setAuthToken } from '../lib/authToken'

interface AuthContextValue {
  session: Session | null
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // onAuthStateChange is the single source of truth.
    // In Supabase v2 it fires synchronously with INITIAL_SESSION on registration,
    // giving us the stored session (or null) before any component can make API calls.
    // We only set loading=false here — never from a separate getSession() call —
    // so PrivateRoute never renders children before the session state is known.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const token = session?.access_token ?? null
      setSession(session)
      setAuthToken(token)
      setLoading(false)
      console.log(`[Auth] ${event}: token=${token ? token.slice(0, 20) + '…' : 'null'}`)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    await supabase.auth.signOut()
    setSession(null)
    setAuthToken(null)
  }

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
